import React from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Box,
  Typography,
  Stack,
} from '@mui/joy';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ImpactSection from './ImpactSection';
import { useDragDropScenario } from '../../hooks/useDragDropScenario';
import type { DragDropContext } from '@consolidocs-ui/utils';
import './DragDropMoveModal.scss';
import { useTranslation } from 'react-i18next';

interface DragDropMoveModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  context?: DragDropContext;
  isLoading?: boolean;
}

/**
 * Custom modal for drag-and-drop file/folder move confirmations
 * Displays formatted move details with clear source, destination, and impact sections
 *
 * Uses scenario-driven architecture for better reusability:
 * - Scenarios are defined in dragDropScenarios.ts and can be extended easily
 * - Impact sections are configurable and data-driven
 * - Button colors and styling are tied to scenario metadata
 */
export const DragDropMoveModal: React.FC<DragDropMoveModalProps> = ({
  open,
  onClose,
  onConfirm,
  context,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  // Get scenario and its configuration
  // NOTE: hooks must be called before any early return to keep hook order stable
  const { config } = useDragDropScenario(context);

  if (!context) {
    return null;
  }

  const { itemName, itemType, sourceLocation, destLocation } = context;

  // Format path with "-" separator instead of ">"
  const formatPath = (path?: string): string => {
    if (!path) return '';
    return path.replace(/\s*>\s*/g, ' - ');
  };

  // Backdrop clicks and Escape must not dismiss a move that is already in
  // flight - the mutation would still land with the modal gone.
  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        variant="outlined"
        role="alertdialog"
        className="drag-drop-move-modal"
      >
        {/* Header */}
        <DialogTitle>
          <WarningRoundedIcon />
          {t('Move')} {itemType === 'file' ? 'File' : 'Folder'}
        </DialogTitle>
        <Divider />

        {/* Content */}
        <DialogContent>
          <Stack spacing={0.9}>
            {/* Item Section */}
            <Box className="item-section">
              <Typography
                level="body-sm"
                sx={{ fontWeight: 700, color: '#666', marginBottom: '4px' }}
              >
                <span role="img" aria-label={t('package')}>
                  📦
                </span>{' '}
                {t('Item')}
              </Typography>
              <Box
                sx={{ paddingLeft: '16px', borderLeft: '3px solid #f0f0f0' }}
              >
                <Typography
                  level="body-sm"
                  sx={{ fontWeight: 500, fontSize: '13px', color: '#222' }}
                >
                  {itemType === 'file' ? (
                    <span role="img" aria-label={t('file')}>
                      📄
                    </span>
                  ) : (
                    <span role="img" aria-label={t('folder')}>
                      📁
                    </span>
                  )}{' '}
                  {itemName}
                </Typography>
              </Box>
            </Box>

            {/* Location Section */}
            <Box className="location-section">
              <Typography
                level="body-sm"
                sx={{ fontWeight: 700, color: '#666', marginBottom: '6px' }}
              >
                <span role="img" aria-label={t('location')}>
                  📍
                </span>{' '}
                {t('From')}{' '}
                <span role="img" aria-label={t('arrow')}>
                  →
                </span>{' '}
                {t('To')}
              </Typography>
              <Stack
                spacing={0.8}
                sx={{ paddingLeft: '16px', borderLeft: '3px solid #f0f0f0' }}
              >
                <Box>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: '#999',
                      textTransform: 'uppercase',
                      fontSize: '10px',
                      fontWeight: 700,
                      marginBottom: '2px',
                    }}
                  >
                    {t('From')}
                  </Typography>
                  <Typography
                    level="body-sm"
                    sx={{ fontSize: '13px', color: '#222', fontWeight: 500 }}
                  >
                    {formatPath(sourceLocation)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: '#999',
                      textTransform: 'uppercase',
                      fontSize: '10px',
                      fontWeight: 700,
                      marginBottom: '2px',
                    }}
                  >
                    {t('To')}
                  </Typography>
                  <Typography
                    level="body-sm"
                    sx={{ fontSize: '13px', color: '#222', fontWeight: 500 }}
                  >
                    {formatPath(destLocation)}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Impact Section - now data-driven and reusable */}
            <ImpactSection config={config} />
          </Stack>
        </DialogContent>

        <Divider />

        {/* Actions - button color is now tied to scenario metadata */}
        <DialogActions>
          <Button
            variant="solid"
            color={config.buttonColor}
            loading={isLoading}
            onClick={onConfirm}
            sx={{ minWidth: '100px' }}
          >
            {t('Confirm Move')}
          </Button>
          <Button
            variant="plain"
            color="neutral"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('Cancel')}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
